import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import FadeUpAnimation from '../../components/base/FadeUpAnimation';
import { useSEO, getSiteUrl } from '../../utils/seo';

const NewsPage = () => {
  useSEO({
    title: 'ニュース — 静かに始まる、小さな変化たち | 2b LLC',
    description: '2b LLCのニュース・お知らせ。プロジェクトの進行や地域での取り組み、日々の気づきや言葉を綴ります。静かな行動の記録をお伝えします。',
    keywords: 'ニュース,お知らせ,プロジェクト,活動報告,2b LLC',
    canonical: getSiteUrl('/news'),
    schema: {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: 'ニュース',
      description: '2b LLCのニュース・お知らせ',
      url: getSiteUrl('/news')
    }
  });

  return (
    <div className="min-h-screen bg-[#F6F3EB]">
      <Header />
      
      <main className="pt-32 pb-20">
        {/* ヒーローセクション */}
        <section 
          className="py-32 bg-[#F6F3EB] relative overflow-hidden"
          style={{
            backgroundImage: 'url(https://public.readdy.ai/ai/img_res/7fe0e471e2c848201053cb6d4d926da3.jpg)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat'
          }}
        >
          <div className="absolute inset-0 bg-[#F6F3EB]/75"></div>
          <div className="max-w-6xl mx-auto px-6 text-center relative z-10">
            <div className="text-center mb-20">
              <FadeUpAnimation duration={1400}>
                <img 
                  src="https://static.readdy.ai/image/721199a06948bc0907bde1e591c4b4e9/191bb64e22d504c48b73a6bcbfddf67c.png"
                  alt="2b LLC"
                  className="w-[480px] h-auto mx-auto mb-12 object-contain"
                />
              </FadeUpAnimation>

              <FadeUpAnimation duration={1400}>
                <h1 className="text-5xl md:text-6xl font-semibold text-[#4A4A48] mb-4" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                  News
                </h1>
                <p className="text-xl md:text-2xl text-[#4A4A48] opacity-80" style={{ fontFamily: 'Zen Kaku Gothic Soft, sans-serif' }}>
                  — 静かに始まる、小さな変化たち。
                </p>
              </FadeUpAnimation>
            </div>
          </div>
        </section>

        {/* ニュース一覧セクション */}
        <section 
          className="py-24 bg-white relative overflow-hidden"
          style={{
            backgroundImage: 'url(https://readdy.ai/api/search-image?query=Soft%20water%20ripples%20expanding%20in%20gentle%20patterns%2C%20peaceful%20aqua%20blue%20surface%20with%20subtle%20waves%2C%20serene%20water%20background%20with%20light%20reflections%2C%20minimalist%20zen-like%20composition%2C%20perfect%20for%20news%20content%20sections%2C%20calm%20and%20professional%20atmosphere&width=1920&height=1080&seq=news-list-bg&orientation=landscape)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat'
          }}
        >
          <div className="absolute inset-0 bg-white/85"></div>
          <div className="max-w-6xl mx-auto px-6 relative z-10">
            <FadeUpAnimation duration={1400}>
              <div className="text-center mb-20 max-w-3xl mx-auto">
                <p className="text-xs md:text-lg leading-[1.75] text-[#4A4A48] mb-6" style={{ fontFamily: 'Zen Kaku Gothic Soft, sans-serif' }}>
                  まだ大きなニュースはありません。<br />
                  でも、水面の下では、いくつもの波紋が静かに広がっています。
                </p>
                <p className="text-xs md:text-lg leading-[1.75] text-[#4A4A48]" style={{ fontFamily: 'Zen Kaku Gothic Soft, sans-serif' }}>
                  プロジェクトの進行や、地域での取り組み、<br />
                  そして日々の気づきや言葉を、ここに綴っていきます。
                </p>
              </div>
            </FadeUpAnimation>

            <FadeUpAnimation duration={1400}>
              <div className="mb-20">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  <div className="bg-white rounded-xl shadow-sm hover:shadow-md transition-all duration-300 hover:scale-[1.02] p-8 text-center relative overflow-hidden">
                    <div className="absolute inset-0 flex items-center justify-center opacity-5">
                      <img 
                        src="https://static.readdy.ai/image/721199a06948bc0907bde1e591c4b4e9/cf8d5eaa743ab4ab1b8484699161d190.png"
                        alt=""
                        className="w-32 h-auto object-contain"
                      />
                    </div>
                    <div className="relative z-10">
                      <p className="text-xs md:text-base text-[#A3A19E] mb-4" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                        Coming soon...
                      </p>
                      <h3 className="text-base md:text-xl font-medium text-[#4A4A48] mb-4" style={{ fontFamily: 'Zen Kaku Gothic Soft, sans-serif' }}>
                        最初のニュースを準備中です
                      </h3>
                      <p className="text-xs md:text-lg text-[#A3A19E] leading-[1.6]" style={{ fontFamily: 'Zen Kaku Gothic Soft, sans-serif' }}>
                        静かな歩みの記録を、<br />
                        丁寧に綴っていきます。
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </FadeUpAnimation>

            <FadeUpAnimation duration={1400}>
              <div className="text-center max-w-3xl mx-auto">
                <h2 className="text-2xl md:text-3xl font-semibold text-[#4A4A48] mb-8" style={{ fontFamily: 'Zen Kaku Gothic Soft, sans-serif' }}>
                  想いをかたちにする、その記録。
                </h2>
                <p className="text-xs md:text-lg leading-[1.75] text-[#4A4A48]" style={{ fontFamily: 'Zen Kaku Gothic Soft, sans-serif' }}>
                  2bは、"静かな行動" を重ねていきます。<br />
                  日々の小さな進展を、ここで丁寧にお伝えします。
                </p>
              </div>
            </FadeUpAnimation>

          </div>
        </section>

        {/* CTAセクション */}
        <section 
          className="py-24 bg-[#4A4A48] relative overflow-hidden"
          style={{
            backgroundImage: 'url(https://readdy.ai/api/search-image?query=Dark%20water%20surface%20with%20gentle%20ripples%20and%20waves%2C%20deep%20blue-black%20aquatic%20background%20with%20subtle%20light%20reflections%2C%20peaceful%20nighttime%20water%20with%20concentric%20circles%2C%20serene%20and%20professional%20atmosphere%2C%20perfect%20for%20dark%20themed%20CTA%20sections&width=1920&height=1080&seq=news-cta-bg&orientation=landscape)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat'
          }}
        >
          <div className="absolute inset-0 bg-[#4A4A48]/85"></div>
          <div className="max-w-4xl mx-auto px-6 text-center relative z-10">
            <FadeUpAnimation duration={1400}>
              <h2 className="text-xl md:text-2xl font-semibold text-white mb-8" style={{ fontFamily: 'Zen Kaku Gothic Soft, sans-serif' }}>
                想いを、仕組みに変えるお手伝いをします。
              </h2>
              <p className="text-xs md:text-lg text-white/90 mb-12 max-w-2xl mx-auto leading-[1.75]" style={{ fontFamily: 'Zen Kaku Gothic Soft, sans-serif' }}>
                「やりたいけど、どこから始めていいか分からない」<br />
                そんな時こそ、私たちの出番です。<br />
                まだ言葉にならない段階でも、お話を聞かせて下さい。
              </p>
              <a
                href="mailto:tobe26.2b@gmail.com"
                className="inline-block bg-white text-[#4A4A48] px-8 py-4 rounded-lg font-medium hover:bg-[#5B5A57] hover:text-white transition-colors duration-300 whitespace-nowrap cursor-pointer"
                style={{ fontFamily: 'Zen Kaku Gothic Soft, sans-serif' }}
              >
                📩 ご相談・お問い合わせはこちら
              </a>
            </FadeUpAnimation>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default NewsPage;
